import { useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Modal from 'LuxKit/Modal/Modal'
import { StyledModalBody } from 'LuxKit/Modal/ModalBody'
import { toBaseFareClass, type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import MaldivesCharterBuyPointsBanner from 'components/Flights/CharterBundle/MaldivesCharterBuyPointsBanner'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import CharterBundleCalendarActions from 'components/OfferPage/Filters/CharterFlight/CharterBundleCalendarActions'
import MaldivesDirectSearchFlightCalendar from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useMaldivesDirectCalendarPricing } from './hooks/useMaldivesDirectCalendarPricing'
import type { DateRange } from './hooks/types'
import { mediaQueryUp } from 'components/utils/breakpoint'

const MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID = 'maldives-direct-search-calendar-modal'

/**
 * The default Modal header wraps `title` in a `<Heading lineClamp={2}>` which
 * applies `display: -webkit-box` and `-webkit-line-clamp: 2`. Putting
 * interactive JSX (FilterChips, SwitchButton) inside that title caused Safari
 * to strictly enforce the clamp, cropping the chips and rendering the
 * line-clamp ellipsis. We keep `title` as a plain string and render the
 * actions via `headerExtension`, then re-arrange the modal header grid on
 * desktop+ so the extension area sits on the same row as the title.
 *
 * The modal header is targeted via `data-testid` (which `Modal` automatically
 * appends `-header` to and forwards to `ModalHeader`) so the override does not
 * depend on styled-components hashes propagating through `Modal` →
 * `ModalBase` → `react-modal`. The doubled attribute selector raises
 * specificity above LuxKit's `> .extension-area` rule.
 */
const StyledMaldivesDirectSearchCalendarModal = styled(Modal)`
  ${mediaQueryUp.tablet} {
    &&& {
      max-width: min(var(--safe-size), ${rem(1550)});
    }
  }

  ${mediaQueryUp.desktop} {
    [data-testid="${MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID}-header"][data-testid] {
      grid-template-columns: min-content auto 1fr min-content;
      grid-template-areas:
        "prepended-action title header-actions appended-action"
        ". subtitle subtitle .";

      > .extension-area {
        grid-area: header-actions;
        margin-top: 0;
        justify-self: end;
        align-self: center;
      }

      @media (max-width: ${rem(1175)}) {
        grid-template-columns: min-content 1fr min-content;
        grid-template-areas:
          "prepended-action title appended-action"
          "header-actions header-actions header-actions"
          ". subtitle .";

        > .extension-area {
          justify-self: start;
          margin-top: ${rem(8)};
        }
      }
    }
  }
`

const StyledMaldivesDirectSearchCalendarModalMobile = styled(Modal)`
  ${StyledModalBody} {
    overflow-x: hidden;
    touch-action: pan-y;
  }
`

interface CalendarSelectionOption {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  fareClass?: FareClassWithReward
}

type CharterBundleCalendarActionsProps = React.ComponentProps<typeof CharterBundleCalendarActions>

interface MaldivesDirectSearchCalendarModalProps {
  isOpen: boolean
  title: string | React.ReactElement
  onClose: () => void
  onDateSelect: (option: CalendarSelectionOption) => void
  nights: number
  rooms: Array<App.Occupants>
  luxPlusEnabled: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClassForSearch: FlightsClassTypes
  selectedFareClass?: FareClassWithReward | null
  priceMode: 'total' | 'perPerson'
  selectedOptionId: string
  canStreamHotelOffers: boolean
  onDurationChange: (nights: number) => void
  durationOptions: CharterBundleCalendarActionsProps['durationOptions']
  onLuxPlusToggle: (enabled: boolean) => void
  onFareClassChange: (fareClass: FareClassWithReward | null) => void
  availableFareClasses: ReadonlyArray<FareClassWithReward>
  rewardSeatsOnly: boolean
  onRewardSeatsOnlyChange: (enabled: boolean) => void
  cabinFilter?: FlightsClassTypes | null
  onCabinFilterChange?: (cabin: FlightsClassTypes | null) => void
}

export default function MaldivesDirectSearchCalendarModal(props: MaldivesDirectSearchCalendarModalProps) {
  const {
    isOpen,
    title,
    onClose,
    onDateSelect,
    nights,
    rooms,
    luxPlusEnabled,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    fareClassForSearch,
    selectedFareClass,
    priceMode,
    selectedOptionId,
    canStreamHotelOffers,
    onDurationChange,
    durationOptions,
    onLuxPlusToggle,
    onFareClassChange,
    availableFareClasses,
    rewardSeatsOnly,
    onRewardSeatsOnlyChange,
    cabinFilter,
    onCabinFilterChange,
  } = props
  const isDesktop = useScreenSizeUp('desktop')
  const { calendarPricingByDate, hotelPriceByCabinAndDate } = useMaldivesDirectCalendarPricing({
    rooms,
    fareClass: fareClassForSearch,
    selectedFareClass: selectedFareClass ? toBaseFareClass(selectedFareClass) : undefined,
    nights,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    dateRangesByMonth,
    disabled: !canStreamHotelOffers || !isOpen,
  })

  const isRewardSeatsEnabled = useRewardSeatsEnabled()

  // Wrap onDateSelect to attach the selected fare class so the URL updates with both
  // the date range and the cabin when a calendar item is selected.
  // If the option already carries a fareClass (e.g. desktop table per-column select), keep it;
  // otherwise fall back to the modal-level dropdown selection.
  const handleDateSelectWithFareClass = useCallback((option: CalendarSelectionOption) => {
    onDateSelect({
      ...option,
      fareClass: option.fareClass ?? selectedFareClass ?? undefined,
    })
  }, [onDateSelect, selectedFareClass])

  if (!isOpen) {
    return null
  }

  if (isDesktop) {
    return (
      <StyledMaldivesDirectSearchCalendarModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        headerExtension={
          <CharterBundleCalendarActions
            nights={nights}
            onDurationChange={onDurationChange}
            durationOptions={durationOptions}
            luxPlusEnabled={luxPlusEnabled}
            onLuxPlusToggle={onLuxPlusToggle}
            cabinFilter={cabinFilter}
            onCabinFilterChange={onCabinFilterChange}
            availableFareClasses={availableFareClasses}
            rewardSeatsOnly={rewardSeatsOnly}
            onRewardSeatsOnlyChange={onRewardSeatsOnlyChange}
          />
        }
        size="max"
        flushListItems
        data-testid={MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID}
      >
        {isRewardSeatsEnabled && <MaldivesCharterBuyPointsBanner />}
        <MaldivesDirectSearchFlightCalendar
          onDateSelect={handleDateSelectWithFareClass}
          nights={nights}
          occupancies={rooms}
          luxPlusEnabled={luxPlusEnabled}
          dateRangesByMonth={dateRangesByMonth}
          selectedMonth={selectedMonth}
          onMonthChange={onMonthChange}
          fareClass={fareClassForSearch}
          selectedFareClass={selectedFareClass ?? undefined}
          priceMode={priceMode}
          selectedOptionId={selectedOptionId}
          mode="search"
          itemBundleDataByDate={calendarPricingByDate}
          hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
          rewardSeatsOnly={rewardSeatsOnly}
          cabinFilter={cabinFilter ?? undefined}
        />
      </StyledMaldivesDirectSearchCalendarModal>
    )
  }

  return (
    <StyledMaldivesDirectSearchCalendarModalMobile
      height="full"
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      isPrimaryActionHidden
      flushListItems
    >
      {isRewardSeatsEnabled && <MaldivesCharterBuyPointsBanner />}
      <MaldivesDirectSearchFlightCalendarMobile
        onDateSelect={onDateSelect}
        nights={nights}
        occupancies={rooms}
        luxPlusEnabled={luxPlusEnabled}
        dateRangesByMonth={dateRangesByMonth}
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        fareClass={fareClassForSearch}
        selectedFareClass={selectedFareClass}
        cabinFilter={cabinFilter ?? undefined}
        onFareClassChange={onFareClassChange}
        priceMode={priceMode}
        selectedOptionId={selectedOptionId}
        mode="search"
        itemBundleDataByDate={calendarPricingByDate}
        rewardSeatsOnly={rewardSeatsOnly}
        hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
        headerActions={
          <CharterBundleCalendarActions
            nights={nights}
            onDurationChange={onDurationChange}
            durationOptions={durationOptions}
            luxPlusEnabled={luxPlusEnabled}
            onLuxPlusToggle={onLuxPlusToggle}
            cabinFilter={cabinFilter}
            onCabinFilterChange={onCabinFilterChange}
            availableFareClasses={availableFareClasses}
            rewardSeatsOnly={rewardSeatsOnly}
            onRewardSeatsOnlyChange={onRewardSeatsOnlyChange}
          />
        }
      />
    </StyledMaldivesDirectSearchCalendarModalMobile>
  )
}
